import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  extractFieldErrors,
  extractResponseDetail,
  isPasswordResetTokenInvalid,
  mapPasswordResetServerFieldErrors,
  resolvePasswordResetRequestError,
} from "../../lib/authFormErrors";
import { isPasswordResetEnabled } from "../../lib/passwordResetEnabled";
import { confirmPasswordReset } from "../../lib/passwordResetApi";
import { stripResetCredentialsFromUrl } from "../../lib/passwordResetUrl";
import PasswordResetUnavailable from "./PasswordResetUnavailable";

const MIN_PASSWORD_LENGTH = 8;

type ResetPhase = "form" | "success" | "invalid_token" | "missing_token";

export default function ResetPassword() {
  useEffect(() => {
    stripResetCredentialsFromUrl();
  }, []);

  if (!isPasswordResetEnabled()) {
    return <PasswordResetUnavailable />;
  }

  return <ResetPasswordForm />;
}

function ResetPasswordForm() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [credentials] = useState(() => ({
    uid: searchParams.get("uid") ?? "",
    token: searchParams.get("token") ?? "",
  }));

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phase, setPhase] = useState<ResetPhase>(() =>
    credentials.uid && credentials.token ? "form" : "missing_token",
  );
  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const resetSchema = z
    .object({
      new_password: z
        .string()
        .min(MIN_PASSWORD_LENGTH, t("passwordReset.minLength", { count: MIN_PASSWORD_LENGTH })),
      confirm_password: z.string().min(1, t("login.passwordRequired")),
    })
    .refine((data) => data.new_password === data.confirm_password, {
      message: t("passwordReset.passwordsMismatch"),
      path: ["confirm_password"],
    });

  type ResetForm = z.infer<typeof resetSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetForm>({
    resolver: zodResolver(resetSchema),
  });

  const onSubmit = async (data: ResetForm) => {
    if (!credentials.uid || !credentials.token) {
      setPhase("missing_token");
      return;
    }

    setFormError(null);
    setServerFieldErrors({});
    setIsSubmitting(true);

    try {
      await confirmPasswordReset({
        uid: credentials.uid,
        token: credentials.token,
        new_password: data.new_password,
      });
      setPhase("success");
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        const responseData = error.response.data;
        const fieldErrors = extractFieldErrors(responseData);
        if (isPasswordResetTokenInvalid(responseData, fieldErrors)) {
          setPhase("invalid_token");
          return;
        }

        const nonField = fieldErrors.non_field_errors?.[0];
        const { mapped, unmatchedMessages } = mapPasswordResetServerFieldErrors(fieldErrors);
        setServerFieldErrors(mapped);

        const unmatchedGeneral = unmatchedMessages[0];
        if (nonField) {
          setFormError(nonField);
        } else if (unmatchedGeneral) {
          setFormError(unmatchedGeneral);
        } else if (
          Object.keys(mapped).length === 0 &&
          !fieldErrors.non_field_errors?.length
        ) {
          setFormError(
            extractResponseDetail(responseData) ?? t("passwordReset.confirmError"),
          );
        }
        return;
      }

      const resolved = resolvePasswordResetRequestError(error, { context: "confirm" });
      if (resolved) {
        setFormError(t(resolved.messageKey));
      } else {
        setFormError(t("errors.server"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (phase === "success") {
    return (
      <div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900">
          {t("passwordReset.successTitle")}
        </h2>
        <p className="mb-6 text-gray-600" role="status">
          {t("passwordReset.successMessage")}
        </p>
        <Link to="/login" className="w-full btn-primary inline-flex justify-center">
          {t("passwordReset.backToLogin")}
        </Link>
      </div>
    );
  }

  if (phase === "invalid_token" || phase === "missing_token") {
    return (
      <div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900">
          {t("passwordReset.invalidTokenTitle")}
        </h2>
        <p className="mb-6 text-gray-600" role="alert">
          {phase === "missing_token"
            ? t("passwordReset.tokenMissing")
            : t("passwordReset.invalidTokenMessage")}
        </p>
        <Link
          to="/forgot-password"
          className="w-full btn-primary inline-flex justify-center"
        >
          {t("passwordReset.requestNewLink")}
        </Link>
      </div>
    );
  }

  const newPasswordServerError = serverFieldErrors.new_password;
  const confirmServerError = serverFieldErrors.confirm_password;

  return (
    <div>
      <h2 className="mb-2 text-2xl font-bold text-gray-900">
        {t("passwordReset.resetTitle")}
      </h2>
      <p className="mb-6 text-gray-600">{t("passwordReset.resetSubtitle")}</p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {formError && (
          <p
            id="reset-password-error"
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
          >
            {formError}
          </p>
        )}

        <div>
          <label htmlFor="new-password" className="label">
            {t("passwordReset.newPassword")}
          </label>
          <div className="relative">
            <input
              {...register("new_password")}
              type={showPassword ? "text" : "password"}
              id="new-password"
              autoComplete="new-password"
              aria-invalid={Boolean(errors.new_password || newPasswordServerError)}
              aria-describedby={
                errors.new_password || newPasswordServerError
                  ? "new-password-error"
                  : "new-password-hint"
              }
              className={`input pr-10 ${errors.new_password || newPasswordServerError ? "input-error" : ""}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute text-gray-400 -translate-y-1/2 right-3 top-1/2 hover:text-gray-600"
              aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          <p id="new-password-hint" className="mt-1 text-xs text-gray-500">
            {t("passwordReset.minLengthHint", { count: MIN_PASSWORD_LENGTH })}
          </p>
          {(errors.new_password || newPasswordServerError) && (
            <p id="new-password-error" className="mt-1 text-sm text-red-500" role="alert">
              {errors.new_password?.message ?? newPasswordServerError}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="confirm-password" className="label">
            {t("passwordReset.confirmPassword")}
          </label>
          <div className="relative">
            <input
              {...register("confirm_password")}
              type={showConfirm ? "text" : "password"}
              id="confirm-password"
              autoComplete="new-password"
              aria-invalid={Boolean(errors.confirm_password || confirmServerError)}
              aria-describedby={
                errors.confirm_password || confirmServerError
                  ? "confirm-password-error"
                  : undefined
              }
              className={`input pr-10 ${errors.confirm_password || confirmServerError ? "input-error" : ""}`}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute text-gray-400 -translate-y-1/2 right-3 top-1/2 hover:text-gray-600"
              aria-label={showConfirm ? t("auth.hidePassword") : t("auth.showPassword")}
            >
              {showConfirm ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          {(errors.confirm_password || confirmServerError) && (
            <p id="confirm-password-error" className="mt-1 text-sm text-red-500" role="alert">
              {errors.confirm_password?.message ?? confirmServerError}
            </p>
          )}
        </div>

        <button type="submit" disabled={isSubmitting} className="w-full btn-primary">
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t("passwordReset.resetting")}
            </>
          ) : (
            t("passwordReset.submitReset")
          )}
        </button>
      </form>
    </div>
  );
}
